import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { toast } from '@/lib/notifications';
import { reconcileSubscriptionFromGatewayCallable } from '@/lib/callables';
import type { TenantId } from '@etiquetachef/types';

type Props = {
  open: boolean;
  tenantId: TenantId;
  tenantName: string;
  onClose: () => void;
};

/**
 * "Sincronizar com o PSP": reconcilia o doc `billing/subscription` do tenant
 * com a verdade do gateway dono dele (Stripe ou Asaas), super-admin.
 *
 * Origem: sem issue — commit aee0b5b48, 2026-06-10 12:30 +0200.
 *
 * Por quê: corrige docs dessincronizados — ex.: um re-checkout que deixou
 * campos da assinatura anterior (status CANCELLED, ciclo/valor antigos,
 * mudança de ciclo agendada residual) sobre uma sub nova viva.
 *
 * Contrato: `reconcileSubscriptionFromGatewayCallable({ tenantId })`, sem
 * outro input. Resultado: `reconciled` com `status`/`billing_cycle` novos, ou
 * "sem assinatura viva → paywall"; `removedTestPayments` (> 0 só em LIVE)
 * entra no toast. Idempotente. Provider que não é Stripe nem Asaas →
 * `failed-precondition` (`provider_not_supported`), mensagem num `Alert` dentro
 * do modal (#1021).
 * Permissão no servidor: `requireTenantOperator` com
 * `staffRoles: ADMIN_PERMISSIONS.manageSubscriptions` (ROOT/FINANCE —
 * `docs/permissions.md`, #795 ADM-7) ou ADMIN do CRM (`crmAdminOnly`).
 *
 * Usado por: `BillingActions` (só venda direta), montado em
 * `routes/TenantDetailPage.tsx` (rota `/tenants/:id`, menu "Tenants"), admin
 * web. Spec: `docs/screens/tenant-detail-admin.md`.
 *
 * Dados: `ReconcileSubscriptionOutput` (ver `lib/callables.ts`);
 * efeitos no servidor em `tenants/{tid}/billing/subscription` e
 * `tenants/{tid}/payments/*` (remove `livemode: false` órfãos), audit
 * `SUBSCRIPTION_RECONCILED`
 * (`apps/functions/src/admin/reconcileSubscriptionFromGateway.ts`).
 *
 * Histórico:
 *  - 2026-06-28 14:42 +0200 — remove pagamentos test-mode órfãos (toast).
 *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function ReconcileSubscriptionDialog({
  open,
  tenantId,
  tenantName,
  onClose,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Limpa o erro e devolve o controle ao pai. */
  function close(): void {
    setError(null);
    onClose();
  }

  /** Chama a reconciliação e monta o toast (inclui test payments removidos). */
  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const result = await reconcileSubscriptionFromGatewayCallable({ tenantId });
      const base = result.reconciled
        ? t('billing.reconcile.successReconciled', {
            status: result.status ?? '—',
            cycle: result.billing_cycle ?? '—',
          })
        : t('billing.reconcile.successPaywalled');
      const removed = result.removedTestPayments ?? 0;
      toast.success(
        removed > 0
          ? `${base} ${t('billing.reconcile.testPaymentsRemoved', { count: removed })}`
          : base,
      );
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('billing.reconcile.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              loading={submitting}
            >
              {t('billing.reconcile.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('billing.reconcile.description', { name: tenantName })}
        </Box>
        <Alert type="info">{t('billing.reconcile.info')}</Alert>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
